/**********************************************************************************************************
*   BASE IMPORTS
**********************************************************************************************************/
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useToaster } from 'react-hot-toast'
import { useAppSelector } from 'store/hooks'

/**********************************************************************************************************
 *   ROUTERS
 **********************************************************************************************************/
import GuestRouter from 'router/guestRouter'
import UserRouter from '../router/userRouter'

/**********************************************************************************************************
 *   ACTIONS/QUERIES
 **********************************************************************************************************/
import { useAcceptInvitationQuery, useInitialiseApplicationQuery } from 'api/app'

/**********************************************************************************************************
 *   COMPONENTS
 **********************************************************************************************************/
import { Alert as AlertComponent, Fade, Theme } from '@nexigendigital/nxui'
import { Loader } from 'components/loader'

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components'

const AppGlobalStyles = createGlobalStyle`
    @import url('https://rsms.me/inter/inter.css');

    html {
        width: 100%;
        font-display: swap;
        text-rendering: geometricPrecision;
        font-feature-settings: "kern" 1; 
        font-kerning: normal; 
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        ${Theme.Functions.setFont('Inter')};

        body {
            min-height: 640px;
            width: 100%;
            display: block;
            margin: 0;
        }

        #app-root {
            width: 100vw;
            height: 100vh;
            overflow: hidden;
        }

        p {
            font-weight: 500;
        }
    }
`

const Alert = {
    Provider: styled.div`
        z-index: 21;
        position: absolute;
        top: 0px;
        right: 0px;
    `
}

const Application = {
    Transition: styled(Fade)`
        position: absolute !important;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background-color: ${props => (props.theme['root--background'])};

        ${props => Theme.Functions.mediaBreakDown(props.theme['breakpoints-md'])} {
            display: block;
        };
    `
}

/**********************************************************************************************************
*   COMPONENT START
**********************************************************************************************************/
export const App = () => {
    const dispatch = useDispatch()
    const { appActiveRouter, appSettings, appSession: { authenticated, two_factor_required, account } } = useAppSelector((state) => state.app)

    /****************************************************
     *  TOAST
     ****************************************************/
    const { toasts, handlers } = useToaster()
    const { calculateOffset, updateHeight } = handlers

    /* ***************************************************
     *  APPROVE ADDITIONAL USER
     ****************************************************/
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    const [skipAcceptInvitation, setSkipAcceptInvitation] = useState(true)
    const { data: acceptInvitationData = {}, isSuccess: acceptInvitationSuccess } = useAcceptInvitationQuery(token, { skip: skipAcceptInvitation })

    useEffect(() => {
        if (token) setSkipAcceptInvitation(false)
    }, [dispatch, token, setSkipAcceptInvitation, acceptInvitationSuccess, acceptInvitationData])

    /* ***************************************************
     *  GET APPLICATION SETTINGS & THEME
     ****************************************************/
    useInitialiseApplicationQuery()

    useEffect(() => {
        if (authenticated && !two_factor_required && account) setSkipAcceptInvitation(true)
    }, [authenticated, two_factor_required, account])

    function appDisplay() {
        switch (appActiveRouter.template) {
            case 'user': return <UserRouter />
            case 'guest': return <GuestRouter />
            case 'loading':
            default: return <Loader width={70} height={70} message={appActiveRouter.meta?.message} />
        }
    }

    /* ***************************************************
     *  GET APPLICATION SETTINGS & THEME
     ****************************************************/
    return (
        <ThemeProvider theme={appSettings.theme}>
            <AppGlobalStyles />
            <Alert.Provider>
                {toasts.map((t) => (
                    <AlertComponent
                        key={t.id}
                        t={t}
                        updateHeight={updateHeight}
                        offset={calculateOffset(t, {
                            reverseOrder: false
                        })}
                    />
                ))}
            </Alert.Provider>
            <Application.Transition when={appActiveRouter.template}>
                {appDisplay()}
            </Application.Transition>
        </ThemeProvider>
    )
}
